
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Align with actual Supabase edges table schema.
export interface Edge {
  id: string;
  source_node_id?: string | null;
  target_node_id?: string | null;
  relationship_type: string;
  created_by?: string | null;
  created_at?: string | null;
  description?: string | null;
  is_bidirectional?: boolean | null;
  metadata?: any | null;
  strength_score?: number | null;
  weight?: number | null;
}

export function useCrossDisciplinaryEdges({
  sourceNodeId,
  targetNodeId,
  relationshipType,
}: {
  sourceNodeId?: string;
  targetNodeId?: string;
  relationshipType?: string;
} = {}) {
  return useQuery({
    queryKey: [
      'cross-disciplinary-edges',
      sourceNodeId,
      targetNodeId,
      relationshipType,
    ],
    queryFn: async () => {
      let q = supabase
        .from('edges')
        .select('*')
        .order('created_at', { ascending: false });
      if (sourceNodeId) q = q.eq('source_node_id', sourceNodeId);
      if (targetNodeId) q = q.eq('target_node_id', targetNodeId);
      if (relationshipType) q = q.eq('relationship_type', relationshipType);
      const { data, error } = await q;
      if (error) throw error;
      return data as Edge[];
    },
  });
}

export function useCreateEdge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Omit<
        Edge,
        'id' | 'created_at'
      >
    ) => {
      // Only insert fields that exist in the table.
      const { data, error } = await supabase
        .from('edges')
        .insert([payload])
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cross-disciplinary-edges'] });
    },
  });
}

export function useUpdateEdge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Edge> & { id: string }) => {
      const { data, error } = await supabase
        .from('edges')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cross-disciplinary-edges'] });
    },
  });
}

export function useDeleteEdge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase.from('edges').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cross-disciplinary-edges'] });
    },
  });
}
